import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdministratorsOnly } from './AdministratorsOnly';
import { AlertTriangle, ArrowUpCircle, CheckCircle2, Info, RefreshCw, XCircle } from 'lucide-react';
import {
  escalationHistoryService,
  type EscalationConfigReport,
  type EscalationHistoryFilters,
  type EscalationHistoryItem,
  type EscalationHistoryPage,
  type EscalationStats,
} from '@services/sla.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

/**
 * Admin escalation history (Task 8) and configuration health (Task 9).
 *
 * The point of this page is stated in the requirement: an admin can verify the
 * escalation feature WITHOUT reading application logs. So it answers two
 * questions that were previously only answerable from a pod's stdout —
 * "what has escalated?" and, when the answer is nothing, "why not?".
 *
 * Tenant-scoped: the tenant comes from the authenticated session, so there is
 * deliberately no tenant selector here. A cross-tenant view belongs on a
 * platform-admin surface with its own audit trail.
 */
const PAGE_SIZE = 25;

/** Business minutes as "4h 12m" — mirrors formatEscalationDuration in shared. */
function formatElapsed(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—';
  const total = Math.max(0, Math.floor(minutes));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const rem = total % 60;
  if (hours < 24) return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const REASON_STYLE: Record<string, string> = {
  approaching_breach: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  breached: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const NOTIFICATION_STYLE: Record<string, string> = {
  delivered: 'text-green-700 dark:text-green-400',
  partial: 'text-amber-700 dark:text-amber-400',
  failed: 'text-red-700 dark:text-red-400',
  // Suppressed is a deliberate, non-error outcome — neutral, not red.
  suppressed: 'text-gray-500 dark:text-gray-400',
  none: 'text-gray-500 dark:text-gray-400',
};

const NOTIFICATION_LABEL: Record<string, string> = {
  delivered: 'Delivered',
  partial: 'Partially delivered',
  failed: 'Delivery failed',
  // Configured, but the recipient's preferences/quiet hours suppressed it —
  // distinct from nothing being configured (#18925).
  suppressed: 'Suppressed by preferences',
  none: 'No notification',
};

function StatCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="flex h-full flex-col justify-between rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${tone ?? 'text-gray-900 dark:text-gray-100'}`}>
        {value}
      </p>
    </div>
  );
}

export function EscalationHistory() {
  const [filters, setFilters] = useState<EscalationHistoryFilters>({});
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<EscalationHistoryPage | null>(null);
  const [stats, setStats] = useState<EscalationStats | null>(null);
  const [config, setConfig] = useState<EscalationConfigReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The server refused this viewer. That is not an error to retry: the page is
  // replaced by the same no-access card every admin-only setting uses, rather
  // than a red banner under filters and a Refresh that can never work.
  const [forbidden, setForbidden] = useState(false);

  const query = useMemo(
    () => ({ ...filters, limit: PAGE_SIZE, offset }),
    [filters, offset],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The three reads are independent; one failing must not blank the others.
      const [list, counters, health] = await Promise.all([
        escalationHistoryService.list(query),
        escalationHistoryService.stats(filters),
        escalationHistoryService.configReport(),
      ]);
      setPage(list);
      setStats(counters);
      setConfig(health);
    } catch (err) {
      // Say what happened in words the reader can act on, never the
      // transport's ("Request failed with status code 403").
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setForbidden(true);
        return;
      }
      setError(
        'Escalation history could not be loaded. Try Refresh; if it keeps failing, contact your administrator.',
      );
    } finally {
      setLoading(false);
    }
  }, [query, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilter = (patch: EscalationHistoryFilters) => {
    setOffset(0);
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const total = page?.total ?? 0;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + PAGE_SIZE, total);

  if (forbidden) {
    return <AdministratorsOnly what="show you the escalation history" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Escalation history
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Every SLA escalation level that has fired, and whether anyone was told.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Refresh
        </button>
      </div>

      {/* Task 9 — why nothing has escalated, when nothing has. This sits ABOVE
          the list on purpose: an empty list is ambiguous, and the explanation
          is the thing an admin actually came here for. */}
      {config && (config.inertPolicies > 0 || config.neverExecutedPolicies > 0 || config.policiesWithErrors > 0) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                Escalation configuration needs attention
              </h3>
              <ul className="mt-2 space-y-2">
                {config.policies
                  .filter((p) => p.inert || p.neverExecuted || p.errorCount > 0 || p.warningCount > 0)
                  .map((p) => (
                    <li key={p.policyId} className="text-sm text-amber-900 dark:text-amber-200">
                      <span className="font-medium">{p.policyName}</span>
                      {p.inert && ' — no escalation levels will ever run.'}
                      {!p.inert && p.neverExecuted &&
                        ` — ${p.levelCount} level${p.levelCount === 1 ? '' : 's'} configured, never executed.`}
                      {p.issues.length > 0 && (
                        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
                          {p.issues.map((issue, i) => (
                            <li key={`${p.policyId}-${i}`}>
                              <span className="font-medium">
                                {issue.severity === 'error' ? 'Error' : 'Warning'}:
                              </span>{' '}
                              {issue.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {stats && (
        <div
          role="group"
          aria-label="Escalation metrics"
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
        >
          <StatCard label="Total escalations" value={stats.total} />
          <StatCard label="Level 1" value={stats.level1} />
          <StatCard label="Level 2" value={stats.level2} />
          <StatCard label="Level 3" value={stats.level3} />
          <StatCard
            label="Approaching breach"
            value={stats.approachingBreach}
            tone="text-amber-700 dark:text-amber-400"
          />
          <StatCard label="Breached" value={stats.breached} tone="text-red-700 dark:text-red-400" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <ThemedSelect
          aria-label="Escalation level"
          value={filters.level ?? ''}
          onChange={(e) => setFilter({ level: e.target.value ? Number(e.target.value) : undefined })}
          className="h-[34px] rounded-md border border-gray-300 bg-[var(--co-raised)] px-2 py-1.5 text-sm text-[var(--co-ink)] dark:border-gray-600"
        >
          <option value="">All levels</option>
          <option value="1">Level 1</option>
          <option value="2">Level 2</option>
          <option value="3">Level 3</option>
        </ThemedSelect>

        <ThemedSelect
          aria-label="Reason"
          value={filters.triggerReason ?? ''}
          onChange={(e) =>
            setFilter({
              triggerReason: (e.target.value || undefined) as EscalationHistoryFilters['triggerReason'],
            })
          }
          className="h-[34px] rounded-md border border-gray-300 bg-[var(--co-raised)] px-2 py-1.5 text-sm text-[var(--co-ink)] dark:border-gray-600"
        >
          <option value="">All reasons</option>
          <option value="approaching_breach">Approaching breach</option>
          <option value="breached">Breached</option>
        </ThemedSelect>

        <ThemedSelect
          aria-label="Delivery status"
          value={filters.status ?? ''}
          onChange={(e) => setFilter({ status: e.target.value || undefined })}
          className="h-[34px] rounded-md border border-gray-300 bg-[var(--co-raised)] px-2 py-1.5 text-sm text-[var(--co-ink)] dark:border-gray-600"
        >
          <option value="">All outcomes</option>
          <option value="succeeded">Succeeded</option>
          <option value="pending_retry">Retrying</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </ThemedSelect>

        <input
          type="date"
          aria-label="From date"
          value={filters.startDate?.slice(0, 10) ?? ''}
          onChange={(e) =>
            setFilter({ startDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })
          }
          className="h-[34px] rounded-md border border-gray-300 bg-[var(--co-raised)] px-2 py-1.5 text-sm text-[var(--co-ink)] dark:border-gray-600"
        />
        <input
          type="date"
          aria-label="To date"
          value={filters.endDate?.slice(0, 10) ?? ''}
          onChange={(e) =>
            setFilter({ endDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })
          }
          className="h-[34px] rounded-md border border-gray-300 bg-[var(--co-raised)] px-2 py-1.5 text-sm text-[var(--co-ink)] dark:border-gray-600"
        />

        {(filters.level || filters.triggerReason || filters.status || filters.startDate || filters.endDate) && (
          <button
            type="button"
            onClick={() => {
              setFilters({});
              setOffset(0);
            }}
            className="rounded-md px-2 py-1.5 text-sm text-gray-600 underline dark:text-gray-300"
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
          <XCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading escalations…</p>}

      {!loading && !error && total === 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            No escalations recorded for this filter.
            {config && config.inertPolicies > 0 &&
              ' At least one SLA policy has no runnable escalation levels — see the warning above.'}
          </span>
        </div>
      )}

      {!loading && total > 0 && (
        <>
          {/* Height-capped so the horizontal scrollbar stays in view: with 25
              rows it otherwise sits below the fold and the right-hand columns
              look cut off rather than scrollable. */}
          <div className="scrollbar-thin max-h-[max(320px,calc(100vh-400px))] overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Ticket', 'Level', 'Reason', 'SLA policy', 'Elapsed', 'Notification', 'When'].map(
                    (h) => (
                      <th
                        key={h}
                        scope="col"
                        className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 whitespace-nowrap bg-white dark:divide-gray-700 dark:bg-gray-900">
                {page!.items.map((row: EscalationHistoryItem) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2">
                      <a
                        href={`/tickets/${row.ticketId}`}
                        className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        #{row.ticket?.ticketNumber ?? row.ticketId.slice(0, 8)}
                      </a>
                      {row.ticket?.subject && (
                        <p className="max-w-[180px] truncate text-xs text-gray-500 min-[1680px]:max-w-xs dark:text-gray-400">
                          {row.ticket.subject}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 font-medium text-gray-900 dark:text-gray-100">
                        <ArrowUpCircle className="h-3.5 w-3.5 text-orange-500" aria-hidden="true" />
                        L{row.level}
                      </span>
                      {row.ruleName && (
                        <p
                          className="max-w-[120px] truncate text-xs text-gray-500 dark:text-gray-400"
                          title={row.ruleName}
                        >
                          {row.ruleName}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                          REASON_STYLE[row.triggerReason] ?? ''
                        }`}
                      >
                        {row.triggerReason === 'approaching_breach' ? 'Approaching breach' : 'SLA breached'}
                      </span>
                    </td>
                    <td
                      className="max-w-[132px] truncate px-3 py-2 text-gray-700 dark:text-gray-300"
                      title={row.slaPolicy?.name}
                    >
                      {row.slaPolicy?.name ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {formatElapsed(row.elapsedBusinessMinutes)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1 text-xs ${
                          NOTIFICATION_STYLE[row.notificationStatus ?? 'none'] ?? ''
                        }`}
                      >
                        {row.notificationStatus === 'delivered' && (
                          <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {row.notificationStatus === 'failed' && (
                          <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                        )}
                        {NOTIFICATION_LABEL[row.notificationStatus ?? 'none'] ?? '—'}
                      </span>
                    </td>
                    <td className="w-[104px] min-w-[104px] whitespace-normal px-3 py-2 text-gray-500 dark:text-gray-400">
                      {formatWhen(row.executedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
            <span>
              Showing {showingFrom}–{showingTo} of {total}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="rounded-md border border-gray-300 px-2 py-1 disabled:opacity-40 dark:border-gray-600"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={showingTo >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="rounded-md border border-gray-300 px-2 py-1 disabled:opacity-40 dark:border-gray-600"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default EscalationHistory;
